"use client";

import { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import MapPreview from "@/components/MapPreview";
import CourseCard, { sortCoursesByAvailability, type Course } from "@/components/CourseCard";
import RegisterModal from "@/components/RegisterModal";
import ModalityCard from "@/components/ModalityCard";
import {
  BookOpen,
  Briefcase,
  Building2,
  CheckCircle2,
  GraduationCap,
  MapPin,
  Phone,
  Search,
} from "lucide-react";

type Center = {
  id: string;
  name: string;
  address: string;
  zip: string;
  phone: string;
  imageUrl?: string;
  mapEmbedHtml?: string;
  displayMode?: "map" | "photo";
};

export default function Home() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [centers, setCenters] = useState<Center[]>([]);

  useEffect(() => {
    async function fetchCourses() {
      const res = await fetch("/api/courses");
      const data = await res.json();
      setCourses(data);
    }
    fetchCourses();
  }, []);

  useEffect(() => {
    async function fetchCenters() {
      const res = await fetch("/api/centers");
      const data = await res.json();
      setCenters(data);
    }
    fetchCenters();
  }, []);

  const publicCourses = useMemo(() => courses.filter((course) => !course.hidden), [courses]);
  const categories = useMemo(
    () => Array.from(new Set(publicCourses.map((course) => course.category))).sort(),
    [publicCourses]
  );
  const locations = useMemo(
    () => Array.from(new Set(publicCourses.map((course) => course.location))).sort(),
    [publicCourses]
  );

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return publicCourses.filter((course) => {
      const matchesQuery = normalized
        ? `${course.title} ${course.category} ${course.shortDescription}`.toLowerCase().includes(normalized)
        : true;
      const matchesCategory = selectedCategory ? course.category === selectedCategory : true;
      const matchesLocation = selectedLocation ? course.location === selectedLocation : true;
      return matchesQuery && matchesCategory && matchesLocation;
    });
  }, [publicCourses, query, selectedCategory, selectedLocation]);

  const sortedCourses = useMemo(() => {
    return sortCoursesByAvailability(filtered);
  }, [filtered]);

  return (
    <>
      <Head>
        <title>Grupo Lincea - Formación profesional en Canarias</title>
        <meta
          name="description"
          content="Cursos profesionales de Grupo Lincea para mejorar tu empleabilidad en Canarias."
        />
      </Head>

      <main className="flex flex-col">
        <section className="lincea-hero flex items-center text-white">
          <div className="container py-20">
            <div className="max-w-3xl">
              <span className="eyebrow border-white/20 bg-white/10 text-white backdrop-blur">
                Formación para avanzar
              </span>
              <h1 className="mt-6 text-5xl font-semibold tracking-tight sm:text-6xl lg:text-7xl">
                Grupo Lincea
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-white/82 sm:text-xl">
                Formación profesional clara, cercana y orientada a resultados para personas que
                quieren mejorar su futuro laboral en Canarias.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#buscador" className="btn-primary bg-[var(--accent)] hover:bg-red-700">
                  Buscar curso <Search className="h-4 w-4" />
                </a>
                <a href="#nuestros-centros" className="btn-secondary border-white/30 bg-white/10 text-white hover:bg-white hover:text-emerald-950">
                  Ver centros <MapPin className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="mt-14 grid max-w-4xl gap-5 sm:grid-cols-3">
              <div className="lincea-stat">
                <p className="text-3xl font-semibold">{publicCourses.length || "..."}</p>
                <p className="mt-1 text-sm text-white/70">cursos activos</p>
              </div>
              <div className="lincea-stat">
                <p className="text-3xl font-semibold">{locations.length || "..."}</p>
                <p className="mt-1 text-sm text-white/70">ubicaciones y modalidades</p>
              </div>
              <div className="lincea-stat">
                <p className="text-3xl font-semibold">{categories.length || "..."}</p>
                <p className="mt-1 text-sm text-white/70">áreas formativas</p>
              </div>
            </div>
          </div>
        </section>

        <section className="lincea-band">
          <div className="container grid gap-4 py-5 text-sm text-emerald-950 md:grid-cols-3">
            <p className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />
              Atención personalizada
            </p>
            <p className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />
              Formación presencial y online
            </p>
            <p className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />
              Cursos conectados con el mercado laboral
            </p>
          </div>
        </section>

        <section id="oferta-formativa" className="section-spacing order-first">
          <div className="container">
            <div className="grid gap-10 lg:grid-cols-[0.75fr,1.25fr] lg:items-end">
              <div>
                <span className="eyebrow">Modalidades</span>
                <h2 className="mt-4 text-4xl font-semibold tracking-tight text-emerald-950">
                  Elige cómo quieres formarte
                </h2>
                <p className="mt-4 text-lg leading-8 text-slate-600">
                  Diferentes sedes y formatos para que la formación encaje con tu disponibilidad,
                  tu ubicación y tus objetivos.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <ModalityCard
                  title="Las Palmas de Gran Canaria"
                  description="Oferta presencial en los centros de Las Palmas, con cursos de distintas familias profesionales."
                  link="/islas/las-palmas-de-gran-canaria"
                  imageUrl="/images/las-palmas-map.png"
                />
                <ModalityCard
                  title="Santa Cruz de Tenerife"
                  description="Cursos disponibles en Tenerife para diferentes perfiles y objetivos profesionales."
                  link="/islas/santa-cruz-de-tenerife"
                  imageUrl="/images/tenerife-map.png"
                />
                <ModalityCard
                  title="Cursos en Vecindario"
                  description="Formación presencial en Gran Canaria con grupos acompañados por equipo docente."
                  link="/islas/vecindario-gran-canaria"
                  imageUrl="/images/vecindario-map.png"
                />
                <ModalityCard
                  title="Cursos online"
                  description="Avanza desde cualquier lugar con propuestas formativas flexibles."
                  link="/islas/online"
                  imageUrl="/images/online.png"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="buscador" className="section-spacing bg-white">
          <div className="container">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div className="max-w-2xl">
                <span className="eyebrow bg-emerald-50">Oferta formativa</span>
                <h2 className="mt-4 text-4xl font-semibold tracking-tight text-emerald-950">
                  Busca tu próxima oportunidad
                </h2>
                <p className="mt-3 text-lg text-slate-600">
                  Filtra por nombre, área o ubicación y solicita información en un clic.
                </p>
              </div>
              <p className="text-sm font-semibold text-emerald-950">
                {filtered.length} curso{filtered.length !== 1 ? "s" : ""} encontrado{filtered.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="mt-8 grid gap-3 rounded-md border border-emerald-900/10 bg-[var(--muted)] p-3 md:grid-cols-[1.2fr,1fr,1fr,auto]">
              <input
                    data-analytics="filter/search"
                type="text"
                className="input-field"
                placeholder="Buscar por nombre, área o descripción"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
              />
              <select
                    data-analytics="filter/category"
                className="input-field"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                }}
              >
                <option value="">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <select
                    data-analytics="filter/location"
                className="input-field"
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                }}
              >
                <option value="">Todos los lugares</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
              <button
                    data-analytics="filter/reset"
                className="btn-secondary whitespace-nowrap"
                onClick={() => {
                  setQuery("");
                  setSelectedCategory("");
                  setSelectedLocation("");
                }}
              >
                Limpiar
              </button>
            </div>

            {filtered.length === 0 ? (
              <div className="mt-8 lincea-panel text-center">
                <h3 className="text-xl font-semibold text-emerald-950">
                  Próximamente puede que este curso esté disponible
                </h3>
                <p className="mt-2 text-slate-600">
                  Te recomendamos estar atento a la web o contactarnos para más información.
                </p>
              </div>
            ) : (
              <>
                <div className="grid-courses mt-8">
                  {sortedCourses.map((course) => (
                    <CourseCard key={course.id} course={course} onRegister={setSelectedCourse} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section id="contacto" className="section-spacing bg-emerald-950 text-white">
          <div className="container grid gap-8 md:grid-cols-[1.2fr,0.8fr] md:items-center">
            <div>
              <span className="eyebrow border-white/20 bg-white/10 text-white">Contacto</span>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight">Tu futuro empieza aquí</h2>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-white/75">
                Si quieres resolver dudas o recibir orientación sobre qué curso encaja mejor contigo,
                el equipo de Grupo Lincea puede ayudarte.
              </p>
            </div>
            <div className="lincea-panel bg-white text-emerald-950">
              <p className="font-semibold">Equipo Grupo Lincea</p>
              <a href="mailto:info@grupolincea.es" className="btn-primary mt-5 w-full">
                Contacta con nosotros
              </a>
              <div className="mt-5 grid gap-2 text-sm text-slate-600">
                <p className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[var(--accent)]" />
                  info@grupolincea.es
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[var(--accent)]" />
                  928 155 749
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="nuestros-centros" className="section-spacing">
          <div className="container">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <span className="eyebrow">Centros</span>
                <h2 className="mt-4 text-4xl font-semibold tracking-tight text-emerald-950">
                  Nuestros centros
                </h2>
                <p className="mt-3 text-lg text-slate-600">
                  Encuentra tu centro de formación más cercano.
                </p>
              </div>
              <Building2 className="hidden h-12 w-12 text-[var(--accent)] md:block" />
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {centers.map((center) => (
                <article key={center.id} className="card flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-emerald-950">{center.name}</h3>
                    <p className="mt-2 text-slate-600">{center.address}</p>
                    <p className="text-slate-600">{center.zip}</p>
                    <p className="mt-2 font-semibold text-emerald-950">Teléfono: {center.phone}</p>
                  </div>
                  {center.displayMode !== "photo" && center.mapEmbedHtml ? (
                    <div className="mt-5 overflow-hidden rounded-md border border-emerald-900/10">
                      <div className="aspect-video w-full">
                        <MapPreview {...center} />
                      </div>
                    </div>
                  ) : center.imageUrl ? (
                    <div className="mt-5 h-52 overflow-hidden rounded-md border border-emerald-900/10">
                      <img src={encodeURI(center.imageUrl)} alt={center.name} className="h-full w-full object-cover" />
                    </div>
                  ) : (
                    <div className="mt-5 flex h-52 items-center justify-center rounded-md bg-emerald-50">
                      <MapPin className="h-14 w-14 text-emerald-800/30" />
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <RegisterModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />
    </>
  );
}
